import { Bot, CheckCircle, Clock, Code, Eye, Globe, Image, MessageSquare, Send, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'

interface AgentCapabilities {
  image: boolean
  audio: boolean
  video: boolean
  code: boolean
  web_search: boolean
  data_analysis: boolean
}

interface ProjectAgent {
  assignment_id: number
  agent_id: number
  agent_name: string
  expertise: string
  bio: string
  level: number
  total_xp: number
  eq_score: number
  iq_score: number
  known_llm: string
  llm_type: string
  qualification_score: number
  hire_eligible: number
  can_process_image: number
  can_process_audio: number
  can_process_video: number
  can_generate_code: number
  can_search_web: number
  online_hours_total: number
  connection_stability_pct: number
  avg_response_quality: number
  social_score: number
  capabilities: AgentCapabilities
  owner_name: string
  owner_id: number
  role: string
  status: string
  assigned_at: string
}

interface ProjectAgentsProps {
  projectId: number
  isResearch?: boolean
}

export default function ProjectAgents({ projectId, isResearch }: ProjectAgentsProps) {
  const { user } = useAuth()
  const [agents, setAgents] = useState<ProjectAgent[]>([])
  const [loading, setLoading] = useState(true)
  const [guidanceAgent, setGuidanceAgent] = useState<number | null>(null)
  const [guidanceText, setGuidanceText] = useState('')
  const [sending, setSending] = useState(false)
  const [sentMsg, setSentMsg] = useState('')

  useEffect(() => {
    void fetch(isResearch ? `${API_URL}/api/research/projects/${projectId}/agents` : `${API_URL}/api/projects/${projectId}/agents`)
      .then(r => { if (!r.ok) throw new Error('not ok'); return r.json() })
      .then((d: { agents?: ProjectAgent[] }) => setAgents(d.agents || []))
      .catch(() => setAgents([]))
      .finally(() => setLoading(false))
  }, [projectId])

  const sendGuidance = async (_agentId: number) => {
    if (!guidanceText.trim() || !user) {return}
    setSending(true)
    try {
      const res = await fetch(
        `${API_URL}/api/projects/${projectId}/messages?content=${encodeURIComponent(guidanceText)}&user_id=${user.id}`,
        { method: 'POST' }
      )
      if (res.ok) {
        setSentMsg('Guidance sent to your agent!')
        setGuidanceText('')
        setTimeout(() => { setGuidanceAgent(null); setSentMsg('') }, 2500)
      }
    } catch {
      setSentMsg('Failed to send')
    } finally {
      setSending(false)
    }
  }

  const getLLMColor = (llm: string) => {
    const map: Record<string, string> = {
      Gemini: '#4285f4', 'GPT-4': '#10a37f', Claude: '#d97706',
      Qwen: '#8b5cf6', Mistral: '#ef4444', Groq: '#f59e0b', Local: 'var(--secondary)',
    }
    return map[llm] || 'var(--secondary)'
  }

  const getQualBadge = (score: number) => {
    if (score >= 70) {return { label: 'Elite', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' }}
    if (score >= 55) {return { label: 'Senior', color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' }}
    if (score >= 40) {return { label: 'Mid', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' }}
    return { label: 'Junior', color: '#10b981', bg: 'rgba(16,185,129,0.1)' }
  }

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading agents...</div>
  }

  if (agents.length === 0) {
    return (
      <div style={{
        padding: '60px 20px', textAlign: 'center',
        background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px',
      }}>
        <Bot size={48} style={{ color: 'var(--text-secondary)', marginBottom: '16px' }} />
        <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>No agents assigned yet</div>
        <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Agents will be automatically assigned by Father Filiz based on research topic.
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
        {agents.length} agent{agents.length !== 1 ? 's' : ''} working on this research
      </div>

      {agents.map(agent => {
        const qual = getQualBadge(agent.qualification_score)
        const isMyAgent = user && String(agent.owner_id) === String(user.id)

        return (
          <div key={agent.assignment_id} style={{
            background: 'var(--card-bg)',
            border: `1px solid ${isMyAgent ? '#667eea' : 'var(--border-color)'}`,
            borderRadius: '14px',
            padding: '20px',
            position: 'relative',
          }}>
            {isMyAgent && (
              <div style={{
                position: 'absolute', top: '12px', right: '12px',
                fontSize: '11px', padding: '3px 10px', borderRadius: '10px',
                background: 'rgba(102,126,234,0.15)', color: '#667eea', fontWeight: '600',
              }}>
                Your Agent
              </div>
            )}

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {/* Avatar */}
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: `linear-gradient(135deg, ${getLLMColor(agent.known_llm)}, ${getLLMColor(agent.known_llm)}88)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: '20px', fontWeight: '700', flexShrink: 0,
              }}>
                {(agent.agent_name || 'A').charAt(0)}
              </div>

              {/* Main info */}
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>{agent.agent_name}</span>
                  <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: qual.bg, color: qual.color, fontWeight: '600' }}>
                    {qual.label}
                  </span>
                  <span style={{
                    fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                    background: `${getLLMColor(agent.known_llm)}20`, color: getLLMColor(agent.known_llm), fontWeight: '600',
                  }}>
                    {agent.known_llm || 'Local LLM'}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  {agent.expertise || 'General AI'} · Owner: {agent.owner_name || 'System'}
                </div>

                {/* XP / Level / EQ row */}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {[
                    { label: 'Level', value: agent.level || 1 },
                    { label: 'XP', value: (agent.total_xp || 0).toLocaleString() },
                    { label: 'EQ', value: (agent.eq_score || 0).toFixed(1) },
                    { label: 'Quality', value: `${(agent.avg_response_quality || 0).toFixed(1)}/10` },
                    { label: 'Online', value: `${(agent.online_hours_total || 0).toFixed(0)}h` },
                    { label: 'Stability', value: `${(agent.connection_stability_pct || 0).toFixed(0)}%` },
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>{s.value}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Capabilities */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {agent.can_generate_code ? <CapBadge icon={<Code size={12} />} label="Code" /> : null}
                  {agent.can_process_image ? <CapBadge icon={<Image size={12} />} label="Image" /> : null}
                  {agent.can_search_web ? <CapBadge icon={<Globe size={12} />} label="Web" /> : null}
                  {agent.can_process_audio ? <CapBadge icon={<Zap size={12} />} label="Audio" /> : null}
                  {agent.can_process_video ? <CapBadge icon={<Eye size={12} />} label="Video" /> : null}
                  <CapBadge icon={<CheckCircle size={12} />} label="Data Analysis" />
                </div>
              </div>

              {/* Role & timing */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', flexShrink: 0 }}>
                <span style={{
                  fontSize: '12px', padding: '4px 12px', borderRadius: '10px',
                  background: agent.status === 'active' ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
                  color: agent.status === 'active' ? '#10b981' : 'var(--secondary)',
                  fontWeight: '600',
                }}>
                  {agent.status || 'active'}
                </span>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Clock size={11} />
                  {new Date(agent.assigned_at).toLocaleDateString()}
                </span>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Role: {agent.role || 'contributor'}
                </span>
              </div>
            </div>

            {/* Owner guidance section */}
            {isMyAgent && (
              <div style={{ marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
                {guidanceAgent === agent.agent_id ? (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Send guidance to {agent.agent_name}
                    </div>
                    <textarea
                      value={guidanceText}
                      onChange={e => setGuidanceText(e.target.value)}
                      placeholder="e.g. Focus on the economic implications of interstellar travel. Prioritize peer-reviewed sources."
                      rows={3}
                      style={{
                        width: '100%', padding: '10px', borderRadius: '8px',
                        border: '1px solid var(--border-color)', background: 'var(--card-bg)',
                        color: 'var(--text-primary)', fontSize: '13px', resize: 'vertical',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        onClick={() => void sendGuidance(agent.agent_id)}
                        disabled={sending || !guidanceText.trim()}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '8px 16px', background: 'linear-gradient(135deg, #667eea, #764ba2)',
                          color: '#fff', border: 'none', borderRadius: '8px',
                          fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                          opacity: sending ? 0.7 : 1,
                        }}
                      >
                        <Send size={14} />
                        {sending ? 'Sending...' : 'Send'}
                      </button>
                      <button
                        onClick={() => { setGuidanceAgent(null); setGuidanceText('') }}
                        style={{
                          padding: '8px 16px', background: 'transparent',
                          color: 'var(--text-secondary)', border: '1px solid var(--border-color)',
                          borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    {sentMsg && (
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#10b981', fontWeight: '500' }}>{sentMsg}</div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setGuidanceAgent(agent.agent_id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px', background: 'rgba(102,126,234,0.1)',
                      color: '#667eea', border: '1px solid rgba(102,126,234,0.3)',
                      borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                    }}
                  >
                    <MessageSquare size={14} />
                    Send guidance to your agent
                  </button>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function CapBadge({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '3px 8px', borderRadius: '6px',
      background: 'rgba(102,126,234,0.08)', color: '#667eea',
      fontSize: '11px', fontWeight: '500',
    }}>
      {icon}{label}
    </span>
  )
}
